// Cave84 — Add Bottle (single-screen redesign)
// Spec §3.1 + §4.1 (F1.1, F1.2, F9.6).
//
// One scrollable screen, no nested modals. Three blocks:
//   Wine     — one search-or-create combobox. Creating a new wine expands an inline
//              card with producer + appellation comboboxes that create-inline
//              (typing a new name reveals country/region in place — no navigation).
//   Bottle   — collapsed by default with a one-line summary; Edit expands it.
//   Purchase — always visible (the fields filled on every add).
//
// All Firestore writes are deferred to submit via useAddBottleFlow → wineGraph.
// Nothing is written while typing, so abandoning the screen leaves no orphans.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  BottleFormat, ClassificationLevel, Currency, FillLevel, LabelCondition,
  PurchaseSource, WineStyle, WineSubStyle, Wine
} from '@cave84/shared-types';
import { InlineCombobox, type ComboOption } from '@/components/InlineCombobox';
import { SuggestInput } from '@/components/SuggestInput';
import { useWines } from '@/hooks/useWines';
import { useProducers, useProducersMap } from '@/hooks/useProducers';
import { useAppellations } from '@/hooks/useAppellations';
import { useCellars } from '@/hooks/useCellars';
import { useCellarLocations } from '@/hooks/useLocations';
import { useVintageByWineAndYear } from '@/hooks/useVintages';
import { useAddBottleFlow } from '@/hooks/useAddBottleFlow';
import { useWishlistMatchForWine, useMarkWishlistAcquired } from '@/hooks/useWishlist';
import type { AddBottleFlowInput, ProducerRef, AppellationRef, WineRef } from '@/services/wineGraph';
import { cuveeKey, findProducerInText, fold, matchesQuery, producerKey } from '@/utils/catalogKeys';
import { foldSearch } from '@/utils/searchText';
import { SOURCE_LABELS } from '@/services/bottleLabels';

// ---------------------------------------------------------------------------
// Label maps — aligned exactly with shared-types union values
// ---------------------------------------------------------------------------

const FORMAT_LABELS: Record<BottleFormat, string> = {
  half: 'Half (375ml)',
  '750ml': 'Standard (750ml)',
  magnum: 'Magnum (1.5L)',
  double_magnum: 'Double Magnum (3L)',
  jeroboam: 'Jeroboam (4.5L)',
  imperial: 'Impériale (6L)',
  salmanazar: 'Salmanazar (9L)',
  methuselah: 'Methuselah (6L Champ.)',
  other: 'Other',
};

const FILL_LABELS: Record<FillLevel, string> = {
  into_neck: 'Into neck',
  base_of_neck: 'Base of neck',
  top_shoulder: 'Top shoulder',
  upper_shoulder: 'Upper shoulder',
  mid_shoulder: 'Mid shoulder',
  low_shoulder: 'Low shoulder',
};

const CONDITION_LABELS: Record<LabelCondition, string> = {
  perfect: 'Perfect',
  minor: 'Minor scuffs',
  scuffed: 'Scuffed',
  torn: 'Torn',
  stained: 'Stained',
  missing: 'Missing',
};

const STYLE_LABELS: Record<WineStyle, string> = {
  red: 'Red', white: 'White', rose: 'Rosé', sparkling: 'Sparkling',
  fortified: 'Fortified', sweet: 'Sweet', orange: 'Orange',
};

const SUB_STYLE_LABELS: Record<WineSubStyle, string> = {
  natural: 'Natural', orange: 'Orange', pet_nat: 'Pét-Nat', sec: 'Sec',
  demi_sec: 'Demi-Sec', doux: 'Doux', extra_brut: 'Extra Brut', brut: 'Brut',
  extra_dry: 'Extra Dry',
};

const CLASSIFICATION_LABELS: Record<ClassificationLevel, string> = {
  grand_cru: 'Grand Cru',
  premier_cru: 'Premier Cru',
  village: 'Village',
  regional: 'Regional',
  generic: 'Generic',
  first_growth: '1st Growth',
  second_growth: '2nd Growth',
  third_growth: '3rd Growth',
  fourth_growth: '4th Growth',
  fifth_growth: '5th Growth',
  cru_bourgeois: 'Cru Bourgeois',
  docg: 'DOCG',
  doc: 'DOC',
  igt: 'IGT',
  monopole: 'Monopole',
  ava: 'AVA (US)',
  gi: 'GI (New World)',
};

const CURRENCIES: Currency[] = ['EUR', 'USD', 'ILS', 'GBP', 'CHF', 'JPY'];
const STYLES = Object.keys(STYLE_LABELS) as WineStyle[];
const COMMON_FORMATS: BottleFormat[] = ['half', '750ml', 'magnum', 'double_magnum'];
const OTHER_FORMATS: BottleFormat[] = ['jeroboam', 'imperial', 'salmanazar', 'methuselah', 'other'];

const eurFormat = new Intl.NumberFormat('en-IE', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
});

/** Best-effort classification detection from an appellation name. User can override. */
function detectClassification(name: string): ClassificationLevel {
  const n = name.toLowerCase();
  if (n.includes('grand cru')) return 'grand_cru';
  if (n.includes('1er') || n.includes('premier cru')) return 'premier_cru';
  if (n.includes('docg')) return 'docg';
  if (/\bigt\b/.test(n)) return 'igt';
  if (/\bdoc\b/.test(n)) return 'doc';
  if (/\bava\b/.test(n)) return 'ava';
  if (n.includes('village')) return 'village';
  return 'regional';
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

const LABEL_CLS = 'block text-[11px] font-medium text-text-tertiary mb-1.5 uppercase tracking-wide';
const INPUT_CLS =
  'w-full ps-3 pe-3 py-2 rounded-md border border-border-medium bg-surface-1 text-text-primary placeholder:text-text-muted text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors';

function Field({ label, hint, help, children }: {
  label: string; hint?: string; help?: string; children: React.ReactNode;
}) {
  return (
    <div>
      <label className={LABEL_CLS}>
        {label}{hint && <span className="normal-case tracking-normal text-text-muted"> ({hint})</span>}
      </label>
      {children}
      {help && <p className="mt-1 text-[11px] leading-snug text-text-muted">{help}</p>}
    </div>
  );
}

function SectionHeader({ n, title, active }: { n: number; title: string; active?: boolean }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className={`w-5 h-5 rounded-full text-[11px] font-medium flex items-center justify-center ${
        active ? 'bg-gold text-black' : 'bg-border-medium text-text-secondary'
      }`}>{n}</span>
      <span className="text-xs uppercase tracking-widest text-text-secondary">{title}</span>
      <span className="flex-1 h-px bg-border-subtle" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AddBottlePage() {
  const navigate = useNavigate();

  // Data sources -----------------------------------------------------------
  const { data: wines = [] } = useWines();
  const producersMap = useProducersMap();
  const { data: producers = [] } = useProducers();
  const { data: appellations = [] } = useAppellations();
  const { data: cellars = [] } = useCellars();

  // Wine resolution --------------------------------------------------------
  const [wineSearch, setWineSearch] = useState('');
  const [selectedWine, setSelectedWine] = useState<Wine | null>(null);
  const [creatingWine, setCreatingWine] = useState(false);

  // New producer -----------------------------------------------------------
  const [producerSearch, setProducerSearch] = useState('');
  const [producerId, setProducerId] = useState<string | null>(null);
  const [creatingProducer, setCreatingProducer] = useState(false);
  const [producerCountry, setProducerCountry] = useState('FR');
  const [producerRegion, setProducerRegion] = useState('');
  const [producerSubregion, setProducerSubregion] = useState('');

  // New appellation --------------------------------------------------------
  const [appSearch, setAppSearch] = useState('');
  const [appId, setAppId] = useState<string | null>(null);
  const [creatingApp, setCreatingApp] = useState(false);
  const [appClassification, setAppClassification] = useState<ClassificationLevel>('regional');
  const [appCountry, setAppCountry] = useState('FR');
  const [appRegion, setAppRegion] = useState('');

  // New wine attributes ----------------------------------------------------
  const [cuvee, setCuvee] = useState('');
  const [vineyard, setVineyard] = useState('');
  const [style, setStyle] = useState<WineStyle>('red');
  const [subStyle, setSubStyle] = useState<WineSubStyle | ''>('');
  const [moreOpen, setMoreOpen] = useState(false);

  // Vintage ----------------------------------------------------------------
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear - 1);
  const yearValid = year >= 1900 && year <= currentYear;
  const vintageQuery = useVintageByWineAndYear(selectedWine?.id, yearValid ? year : undefined);

  // Bottle (collapsed) -----------------------------------------------------
  const [bottleOpen, setBottleOpen] = useState(false);
  const [format, setFormat] = useState<BottleFormat>('750ml');
  const [fillLevel, setFillLevel] = useState<FillLevel>('into_neck');
  const [labelCondition, setLabelCondition] = useState<LabelCondition>('perfect');
  const [capsuleCondition, setCapsuleCondition] = useState<LabelCondition>('perfect');
  const [inOWC, setInOWC] = useState(false);
  const [isSpecial, setIsSpecial] = useState(false);
  const [specialReason, setSpecialReason] = useState('');
  const [notes, setNotes] = useState('');

  // Purchase (always visible) ---------------------------------------------
  const today = new Date().toISOString().slice(0, 10);
  const [purchaseSource, setPurchaseSource] = useState<PurchaseSource>('retail');
  const [sourceDetail, setSourceDetail] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(today);
  const [qty, setQty] = useState(1);
  const [costLanded, setCostLanded] = useState(0);
  const [costCurrency, setCostCurrency] = useState<Currency>('EUR');
  const [costEur, setCostEur] = useState(0);
  const [cellarId, setCellarId] = useState('');
  const [locationId, setLocationId] = useState('');
  const { data: locations = [] } = useCellarLocations(cellarId || undefined);

  // Wishlist (F9.6) — only meaningful for an existing wine ------------------
  const [wishlistConfirmed, setWishlistConfirmed] = useState(false);
  const { data: wishlistMatches = [] } = useWishlistMatchForWine(selectedWine?.id, year);
  const markAcquired = useMarkWishlistAcquired();

  const addFlow = useAddBottleFlow();

  // Combobox option lists --------------------------------------------------
  const wineOptions: ComboOption[] = useMemo(() => {
    const q = wineSearch.trim();
    const appName = new Map(appellations.map(a => [a.id, a.name]));
    return wines
      .filter(w => {
        if (!q) return true;
        // Every word must appear somewhere in producer (+ aliases), appellation,
        // cuvée or vineyard — so "domain denis mortet" or "mortet lavaux" both work.
        const p = producersMap.get(w.producer_id);
        return matchesQuery(q, [p?.name, ...(p?.aliases ?? []), appName.get(w.appellation_id),
          w.cuvee_name, w.vineyard_specific].filter(Boolean).join(' '));
      })
      .slice(0, 12)
      .map(w => ({
        id: w.id,
        label: w.cuvee_name ?? '(no cuvée)',
        sublabel: producersMap.get(w.producer_id)?.name,
        badge: `${w.bottle_count} btl`,
      }));
  }, [wines, wineSearch, producersMap, appellations]);

  const producerOptions: ComboOption[] = useMemo(() => {
    const q = producerSearch.trim().toLowerCase();
    if (!q) return [];
    return producers
      // Accent/punctuation-insensitive, and "Domaine Denis Mortet" finds "Denis Mortet"
      // (same producerKey) — so the existing producer is offered before "Create new".
      .filter(p => {
        const fq = fold(q), qKey = producerKey(q);
        return [p.name, ...p.aliases].some(n => fold(n).includes(fq) || (qKey !== '' && producerKey(n) === qKey));
      })
      .slice(0, 8)
      .map(p => ({ id: p.id, label: p.name, sublabel: `${p.country} · ${p.region}` }));
  }, [producers, producerSearch]);

  const appOptions: ComboOption[] = useMemo(() => {
    const q = foldSearch(appSearch.trim());
    if (!q) return [];
    return appellations
      .filter(a => foldSearch(a.name).includes(q) || foldSearch(a.hierarchy_path).includes(q))
      .slice(0, 8)
      .map(a => ({
        id: a.id,
        label: a.name,
        sublabel: a.hierarchy_path,
        badge: CLASSIFICATION_LABELS[a.classification_level],
      }));
  }, [appellations, appSearch]);

  // Free-text suggestions (distinct existing values) — avoid dupes, allow new
  const regionSuggestions = useMemo(() => {
    const set = new Set<string>();
    producers.forEach(p => { if (p.region) set.add(p.region); });
    appellations.forEach(a => { if (a.region) set.add(a.region); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [producers, appellations]);

  const subregionSuggestions = useMemo(() => {
    const set = new Set<string>();
    producers.forEach(p => { if (p.subregion) set.add(p.subregion); });
    appellations.forEach(a => { if (a.subregion) set.add(a.subregion); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [producers, appellations]);

  // Live composed name for the new wine (producer — appellation, cuvée)
  const newWineName = useMemo(() => {
    const prod = producerId ? (producersMap.get(producerId)?.name ?? producerSearch) : producerSearch.trim();
    const app = appId ? (appellations.find(a => a.id === appId)?.name ?? appSearch) : appSearch.trim();
    let base = [prod, app].filter(Boolean).join(' — ');
    if (cuvee.trim()) base += base ? `, ${cuvee.trim()}` : cuvee.trim();
    return base;
  }, [producerId, producerSearch, appId, appSearch, appellations, producersMap, cuvee]);

  // Handlers ---------------------------------------------------------------
  function pickWine(option: ComboOption) {
    const wine = wines.find(w => w.id === option.id) ?? null;
    setSelectedWine(wine);
    setWineSearch(option.label);
    setCreatingWine(false);
  }
  function clearWine() {
    setSelectedWine(null);
    setWineSearch('');
    setWishlistConfirmed(false);
  }
  function startNewWine() {
    setSelectedWine(null);
    setCreatingWine(true);
    // The search text is usually "producer [+ cuvée]". If it names an existing
    // producer, pick that producer and keep only the remainder as the cuvée.
    // Otherwise it is most likely a NEW producer's name — put it in the
    // producer field (the first thing a new wine needs), never in the cuvée.
    const text = wineSearch.trim();
    const hit = findProducerInText(text, producers);
    if (hit) {
      setProducerId(hit.producer.id);
      setProducerSearch(hit.producer.name);
      setCreatingProducer(false);
      setCuvee(hit.rest);
    } else {
      setProducerId(null);
      setProducerSearch(text);
      setCuvee('');
    }
  }
  function cancelNewWine() {
    setCreatingWine(false);
    setProducerSearch(''); setProducerId(null); setCreatingProducer(false);
    setProducerRegion(''); setProducerSubregion('');
    setAppSearch(''); setAppId(null); setCreatingApp(false); setAppRegion('');
    setCuvee(''); setVineyard(''); setStyle('red'); setSubStyle(''); setMoreOpen(false);
  }

  function pickProducer(option: ComboOption) {
    setProducerId(option.id); setProducerSearch(option.label); setCreatingProducer(false);
  }
  function startNewProducer() {
    setCreatingProducer(true); setProducerId(null);
  }
  function pickAppellation(option: ComboOption) {
    setAppId(option.id); setAppSearch(option.label); setCreatingApp(false);
  }
  function startNewAppellation() {
    setCreatingApp(true); setAppId(null);
    setAppClassification(detectClassification(appSearch));
  }

  function updateCost(value: number) {
    setCostLanded(value);
    if (costCurrency === 'EUR') setCostEur(value);
  }
  function updateCurrency(next: Currency) {
    setCostCurrency(next);
    if (next === 'EUR') setCostEur(costLanded);
  }

  // Duplicate guards (shared rules in utils/catalogKeys) ---------------------
  // Creating a producer / wine that already exists under another spelling is
  // exactly how the catalog got duplicates. The form never creates one silently:
  // it names the existing entry, offers it, and blocks submit until resolved.
  const duplicateProducers = useMemo(() => {
    if (!creatingProducer || producerId || !producerSearch.trim()) return [];
    const key = producerKey(producerSearch);
    if (!key) return [];
    return producers.filter(p => [p.name, ...p.aliases].some(n => producerKey(n) === key));
  }, [creatingProducer, producerId, producerSearch, producers]);

  const duplicateWines = useMemo(() => {
    if (!creatingWine || !producerId) return [];
    const producerName = producersMap.get(producerId)?.name ?? '';
    const appName = appId ? (appellations.find(a => a.id === appId)?.name ?? '') : appSearch;
    const key = cuveeKey(`${appName} ${cuvee} ${vineyard}`, [producerName]);
    return wines.filter(w => {
      if (w.producer_id !== producerId || w.style !== style) return false;
      const wApp = appellations.find(a => a.id === w.appellation_id)?.name ?? '';
      return cuveeKey(`${wApp} ${w.cuvee_name ?? ''} ${w.vineyard_specific ?? ''}`, [producerName]) === key;
    });
  }, [creatingWine, producerId, producersMap, appId, appSearch, appellations, cuvee, vineyard, style, wines]);

  // Validation -------------------------------------------------------------
  const producerValid = producerId != null ||
    (creatingProducer && producerSearch.trim().length > 0 && producerCountry.trim().length === 2 && producerRegion.trim().length > 0);
  const appValid = appId != null || (creatingApp && appSearch.trim().length > 0);
  const newWineValid = creatingWine && producerValid && appValid;
  const wineValid = selectedWine != null || newWineValid;
  const costValid = costCurrency === 'EUR' || costEur > 0;
  const canSubmit = wineValid && yearValid && !!cellarId && costValid && qty >= 1 && !addFlow.isPending
    && duplicateProducers.length === 0 && duplicateWines.length === 0;

  // Display name for the summary line -------------------------------------
  const displayName = selectedWine?.cuvee_name
    ?? (creatingWine ? (cuvee.trim() || appSearch.trim() || producerSearch.trim() || 'New wine') : '—');
  const totalEur = costEur * qty;

  // Submit -----------------------------------------------------------------
  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    let wineRef: WineRef;
    if (selectedWine) {
      wineRef = { kind: 'existing', id: selectedWine.id };
    } else {
      const producerRef: ProducerRef = producerId
        ? { kind: 'existing', id: producerId }
        : { kind: 'new', name: producerSearch.trim(), country: producerCountry, region: producerRegion, subregion: producerSubregion || undefined };
      const appellationRef: AppellationRef = appId
        ? { kind: 'existing', id: appId }
        : { kind: 'new', name: appSearch.trim(), classification_level: appClassification, country: appCountry, region: appRegion };
      wineRef = {
        kind: 'new',
        spec: {
          producer: producerRef,
          appellation: appellationRef,
          cuvee_name: cuvee || undefined,
          vineyard_specific: vineyard || undefined,
          style,
          sub_style: subStyle || undefined,
          grape_composition: [],
        },
      };
    }

    const input: AddBottleFlowInput = {
      wine: wineRef,
      year,
      qty,
      bottle: {
        format, fill_level: fillLevel, label_condition: labelCondition,
        capsule_condition: capsuleCondition, in_OWC: inOWC,
        is_special_occasion: isSpecial, special_occasion_reason: specialReason || undefined,
        notes: notes || undefined,
        purchase_date: new Date(purchaseDate),
        purchase_source: purchaseSource, purchase_source_detail: sourceDetail || undefined,
        cost_landed: costLanded, cost_currency: costCurrency, cost_eur_at_purchase: costEur,
        current_cellar_id: cellarId, current_location_id: locationId || undefined,
      },
    };

    const result = await addFlow.mutateAsync(input);

    if (wishlistConfirmed && wishlistMatches.length > 0) {
      await Promise.all(
        wishlistMatches.map(entry => markAcquired.mutateAsync({ id: entry.id, bottleIds: result.bottleIds }))
      );
    }
    navigate(`/wines/${result.wineId}?vintage=${year}`);
  }

  // Render -----------------------------------------------------------------
  return (
    <div className="min-h-screen bg-surface-0 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-surface-1 border-b border-border-medium px-4 py-3 flex items-center gap-3">
        <button type="button" onClick={() => navigate(-1)}
          className="text-text-secondary hover:text-text-primary transition-colors" aria-label="Back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className="w-5 h-5"><path d="M15 18 9 12l6-6" /></svg>
        </button>
        <div className="flex-1">
          <h1 className="text-base font-semibold text-text-primary">Add bottle</h1>
          <p className="text-xs text-text-muted">One screen · smart defaults</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 px-4 py-6 pb-28 max-w-lg mx-auto w-full">

        {/* ── Section 1: Wine ─────────────────────────────────────────── */}
        <SectionHeader n={1} title="Wine" active />

        {!creatingWine && (
          <Field label="Search wine">
            <InlineCombobox
              value={wineSearch}
              onValueChange={setWineSearch}
              options={wineOptions}
              selectedId={selectedWine?.id ?? null}
              onPick={pickWine}
              onClear={clearWine}
              onCreate={startNewWine}
              createLabel="Create new wine"
              placeholder="Producer, cuvée…"
              initialFocus
            />
          </Field>
        )}

        {creatingWine && (
          <div className="rounded-xl border border-border-medium bg-surface-1 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-widest text-text-tertiary">New wine details</span>
              <button type="button" onClick={cancelNewWine}
                className="text-xs text-text-muted hover:text-gold transition-colors">cancel</button>
            </div>

            {/* Producer */}
            <Field label="Producer">
              <InlineCombobox
                value={producerSearch}
                onValueChange={t => { setProducerSearch(t); setProducerId(null); }}
                options={producerOptions}
                selectedId={producerId}
                onPick={pickProducer}
                onClear={() => { setProducerId(null); setProducerSearch(''); setCreatingProducer(false); }}
                onCreate={startNewProducer}
                createLabel="Create new producer"
                placeholder="e.g. Sylvain Cathiard"
                initialFocus
              />
              {duplicateProducers.length > 0 && (
                <div className="mt-2 rounded-md bg-red-950/25 border border-red-800/60 px-2.5 py-2 space-y-1.5">
                  <p className="text-xs text-red-300">
                    This producer already exists — use it instead of creating a duplicate:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {duplicateProducers.map(p => (
                      <button key={p.id} type="button"
                        onClick={() => pickProducer({ id: p.id, label: p.name })}
                        className="px-2 py-0.5 rounded text-xs border border-red-700/60 text-red-200 hover:bg-red-900/40">
                        Use “{p.name}”
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {creatingProducer && !producerId && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2 rounded-md bg-surface-3 border border-border-subtle px-2.5 py-1.5">
                    <span className="text-gold text-xs">New producer — add its region below</span>
                  </div>
                  <div className="flex gap-2">
                    <div className="w-20">
                      <Field label="Country">
                        <input value={producerCountry} maxLength={2}
                          onChange={e => setProducerCountry(e.target.value.toUpperCase())}
                          placeholder="FR" className={INPUT_CLS} />
                      </Field>
                    </div>
                    <div className="flex-1">
                      <Field label="Region">
                        <SuggestInput value={producerRegion} onValueChange={setProducerRegion}
                          suggestions={regionSuggestions} placeholder="e.g. Burgundy, Rhône, Barossa" />
                      </Field>
                    </div>
                  </div>
                  <Field label="Subregion" hint="optional">
                    <SuggestInput value={producerSubregion} onValueChange={setProducerSubregion}
                      suggestions={subregionSuggestions} placeholder="e.g. Côte de Nuits, Southern Rhône" />
                  </Field>
                </div>
              )}
            </Field>

            {/* Appellation */}
            <Field label="Appellation / origin">
              <InlineCombobox
                value={appSearch}
                onValueChange={t => { setAppSearch(t); setAppId(null); }}
                options={appOptions}
                selectedId={appId}
                onPick={pickAppellation}
                onClear={() => { setAppId(null); setAppSearch(''); setCreatingApp(false); }}
                onCreate={startNewAppellation}
                createLabel="Create new appellation"
                placeholder="e.g. Châteauneuf-du-Pape, Barolo, Napa Valley"
              />
              <p className="mt-1 text-[11px] leading-snug text-text-muted">
                The origin denomination — AOC, DOCG, DO, AVA, GI. This is the wine's official name of place (e.g. Châteauneuf-du-Pape, Barossa Valley), not the region.
              </p>
              {creatingApp && !appId && (
                <div className="mt-2 space-y-2">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Field label="Classification">
                        <select value={appClassification}
                          onChange={e => setAppClassification(e.target.value as ClassificationLevel)}
                          className={INPUT_CLS}>
                          {(Object.keys(CLASSIFICATION_LABELS) as ClassificationLevel[]).map(k => (
                            <option key={k} value={k}>{CLASSIFICATION_LABELS[k]}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <div className="w-20">
                      <Field label="Country">
                        <input value={appCountry} maxLength={2}
                          onChange={e => setAppCountry(e.target.value.toUpperCase())}
                          placeholder="FR" className={INPUT_CLS} />
                      </Field>
                    </div>
                  </div>
                  <Field label="Region" hint="optional">
                    <SuggestInput value={appRegion} onValueChange={setAppRegion}
                      suggestions={regionSuggestions} placeholder={producerRegion || 'e.g. Southern Rhône'} />
                  </Field>
                </div>
              )}
            </Field>

            {/* Cuvée / vineyard */}
            <Field label="Cuvée / bottling name" hint="optional"
              help="A specific bottling within the producer + appellation, if any (e.g. Hommage, Grange, Monte Bello). Leave empty if the appellation is the wine.">
              <input value={cuvee} onChange={e => setCuvee(e.target.value)}
                placeholder="e.g. Hommage à Jacques Perrin" className={INPUT_CLS} />
            </Field>

            {duplicateWines.length > 0 && (
              <div className="rounded-md bg-red-950/25 border border-red-800/60 px-3 py-2 space-y-1.5">
                <p className="text-xs text-red-300">
                  This wine already exists for this producer — add the bottle to it instead of creating a duplicate:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {duplicateWines.map(w => (
                    <button key={w.id} type="button"
                      onClick={() => pickWine({ id: w.id, label: w.cuvee_name ?? '(no cuvée)' })}
                      className="px-2 py-0.5 rounded text-xs border border-red-700/60 text-red-200 hover:bg-red-900/40">
                      Use “{w.cuvee_name ?? '(no cuvée)'}”
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Live name preview */}
            {newWineName && (
              <div className="rounded-md bg-surface-3 border border-border-subtle px-3 py-2">
                <span className="text-[11px] uppercase tracking-wide text-text-tertiary">Will be saved as</span>
                <p className="text-sm text-text-primary mt-0.5" dir="ltr">{newWineName}</p>
              </div>
            )}

            {/* Style */}
            <div>
              <span className={LABEL_CLS}>Style</span>
              <div className="flex flex-wrap gap-2">
                {STYLES.map(s => (
                  <button key={s} type="button" onClick={() => setStyle(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      style === s ? 'border-gold bg-gold/10 text-gold' : 'border-border-medium bg-surface-2 text-text-secondary hover:border-border-strong'
                    }`}>{STYLE_LABELS[s]}</button>
                ))}
              </div>
            </div>

            {/* More details */}
            <button type="button" onClick={() => setMoreOpen(v => !v)}
              className="text-xs text-gold hover:underline">
              {moreOpen ? '− Hide extra details' : '+ More details (vineyard, sub-style)'}
            </button>
            {moreOpen && (
              <div className="space-y-3">
                <Field label="Vineyard / climat" hint="lieu-dit">
                  <input value={vineyard} onChange={e => setVineyard(e.target.value)}
                    placeholder="Specific parcel…" className={INPUT_CLS} />
                </Field>
                <Field label="Sub-style" hint="optional">
                  <select value={subStyle} onChange={e => setSubStyle(e.target.value as WineSubStyle | '')}
                    className={INPUT_CLS}>
                    <option value="">None</option>
                    {(Object.keys(SUB_STYLE_LABELS) as WineSubStyle[]).map(k => (
                      <option key={k} value={k}>{SUB_STYLE_LABELS[k]}</option>
                    ))}
                  </select>
                </Field>
              </div>
            )}
          </div>
        )}

        {/* Vintage */}
        <div className="mt-4 flex items-start gap-3">
          <div className="w-28">
            <Field label="Vintage">
              <input type="number" value={year} min={1900} max={currentYear}
                onChange={e => setYear(Number(e.target.value))}
                className="w-full ps-3 pe-3 py-2 rounded-md border border-border-medium bg-surface-1 text-text-primary text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" />
            </Field>
          </div>
          <div className="flex-1 pt-6 text-xs">
            {selectedWine && vintageQuery.data && (
              <p className="text-emerald-400">✓ Vintage {year} already in cave — bottles add to it</p>
            )}
            {selectedWine && vintageQuery.data === null && !vintageQuery.isLoading && yearValid && (
              <p className="text-text-muted">New vintage — drinking window set to defaults, editable later</p>
            )}
            {!yearValid && <p className="text-claret">Enter a year between 1900 and {currentYear}</p>}
          </div>
        </div>

        {/* Wishlist banner (F9.6) */}
        {selectedWine && wishlistMatches.length > 0 && (
          <div className="mt-4 rounded-lg border border-gold/40 bg-gold/5 px-4 py-3 flex items-start gap-3">
            <span className="text-gold text-lg flex-shrink-0">★</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gold">{selectedWine.cuvee_name} is on your wishlist</p>
              <p className="text-xs text-text-secondary mt-0.5">
                Mark {wishlistMatches.length === 1 ? 'this entry' : `${wishlistMatches.length} entries`} as acquired when done?
              </p>
            </div>
            <button type="button" onClick={() => setWishlistConfirmed(v => !v)}
              className={`flex-shrink-0 px-3 py-1.5 rounded border text-xs font-medium transition-colors ${
                wishlistConfirmed ? 'border-gold bg-gold/20 text-gold' : 'border-border-medium bg-surface-2 text-text-muted hover:border-gold/40 hover:text-gold'
              }`}>
              {wishlistConfirmed ? 'Yes, mark acquired' : 'Mark acquired?'}
            </button>
          </div>
        )}

        {/* ── Section 2: Bottle (collapsed) ───────────────────────────── */}
        <div className="mt-8">
          <SectionHeader n={2} title="Bottle" />
          {!bottleOpen ? (
            <button type="button" onClick={() => setBottleOpen(true)}
              className="w-full flex items-center justify-between rounded-lg border border-border-subtle bg-surface-2 px-4 py-3 hover:border-border-medium transition-colors">
              <span className="text-xs text-text-secondary truncate">
                {FORMAT_LABELS[format]} · {FILL_LABELS[fillLevel].toLowerCase()} · label {CONDITION_LABELS[labelCondition].toLowerCase()}{inOWC ? ' · OWC' : ''}
              </span>
              <span className="text-xs text-gold flex-shrink-0 ms-3">Edit</span>
            </button>
          ) : (
            <div className="rounded-xl border border-border-medium bg-surface-1 p-4 space-y-4">
              {/* Format */}
              <div>
                <span className={LABEL_CLS}>Format</span>
                <div className="flex flex-wrap gap-2">
                  {COMMON_FORMATS.map(f => (
                    <button key={f} type="button" onClick={() => setFormat(f)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        format === f ? 'border-gold bg-gold/10 text-gold' : 'border-border-medium bg-surface-2 text-text-secondary hover:border-border-strong'
                      }`}>{FORMAT_LABELS[f]}</button>
                  ))}
                  <select value={OTHER_FORMATS.includes(format) ? format : ''}
                    onChange={e => e.target.value && setFormat(e.target.value as BottleFormat)}
                    className={`px-3 py-1.5 rounded-full text-xs border focus:outline-none ${
                      OTHER_FORMATS.includes(format) ? 'border-gold bg-gold/10 text-gold' : 'border-border-medium bg-surface-2 text-text-secondary'
                    }`}>
                    <option value="">More…</option>
                    {OTHER_FORMATS.map(f => <option key={f} value={f}>{FORMAT_LABELS[f]}</option>)}
                  </select>
                </div>
              </div>
              {/* Fill */}
              <Field label="Fill level">
                <select value={fillLevel} onChange={e => setFillLevel(e.target.value as FillLevel)} className={INPUT_CLS}>
                  {(Object.keys(FILL_LABELS) as FillLevel[]).map(k => <option key={k} value={k}>{FILL_LABELS[k]}</option>)}
                </select>
              </Field>
              {/* Conditions */}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Label">
                  <select value={labelCondition} onChange={e => setLabelCondition(e.target.value as LabelCondition)} className={INPUT_CLS}>
                    {(Object.keys(CONDITION_LABELS) as LabelCondition[]).map(k => <option key={k} value={k}>{CONDITION_LABELS[k]}</option>)}
                  </select>
                </Field>
                <Field label="Capsule">
                  <select value={capsuleCondition} onChange={e => setCapsuleCondition(e.target.value as LabelCondition)} className={INPUT_CLS}>
                    {(Object.keys(CONDITION_LABELS) as LabelCondition[]).map(k => <option key={k} value={k}>{CONDITION_LABELS[k]}</option>)}
                  </select>
                </Field>
              </div>
              {/* Toggles */}
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={inOWC} onChange={e => setInOWC(e.target.checked)} className="w-4 h-4 accent-gold" />
                  <span className="text-sm text-text-primary">In OWC (original wooden case)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={isSpecial} onChange={e => setIsSpecial(e.target.checked)} className="w-4 h-4 accent-gold" />
                  <span className="text-sm text-text-primary">Special occasion bottle</span>
                </label>
                {isSpecial && (
                  <input value={specialReason} onChange={e => setSpecialReason(e.target.value)}
                    placeholder="Anniversary, milestone…" className={`ms-6 ${INPUT_CLS}`} style={{ width: 'calc(100% - 1.5rem)' }} />
                )}
              </div>
              {/* Notes */}
              <Field label="Notes" hint="optional">
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                  className={`${INPUT_CLS} resize-none`} />
              </Field>
              <button type="button" onClick={() => setBottleOpen(false)}
                className="text-xs text-gold hover:underline">Done — collapse</button>
            </div>
          )}
        </div>

        {/* ── Section 3: Purchase & cellar ────────────────────────────── */}
        <div className="mt-8">
          <SectionHeader n={3} title="Purchase & cellar" />
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Source">
                <select value={purchaseSource} onChange={e => setPurchaseSource(e.target.value as PurchaseSource)} className={INPUT_CLS}>
                  {(Object.keys(SOURCE_LABELS) as PurchaseSource[]).map(s => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
                </select>
              </Field>
              <Field label="Purchase date">
                <input type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)} className={INPUT_CLS} />
              </Field>
            </div>

            <Field label="Source detail" hint="optional">
              <input value={sourceDetail} onChange={e => setSourceDetail(e.target.value)}
                placeholder="Auction lot, merchant name…" className={INPUT_CLS} />
            </Field>

            <div className="grid grid-cols-3 gap-3">
              <Field label="Qty">
                <input type="number" value={qty} min={1} max={24}
                  onChange={e => setQty(Math.max(1, Math.min(24, Number(e.target.value))))} className={INPUT_CLS} />
              </Field>
              <Field label="Cost / btl">
                <input type="number" value={costLanded || ''} min={0} step={0.01}
                  onChange={e => updateCost(Number(e.target.value))} className={INPUT_CLS} />
              </Field>
              <Field label="Currency">
                <select value={costCurrency} onChange={e => updateCurrency(e.target.value as Currency)} className={INPUT_CLS}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>

            {costCurrency !== 'EUR' && (
              <Field label="EUR equivalent (at purchase)">
                <input type="number" value={costEur || ''} min={0} step={0.01}
                  onChange={e => setCostEur(Number(e.target.value))} className={INPUT_CLS} />
              </Field>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Cellar">
                <select value={cellarId}
                  onChange={e => { setCellarId(e.target.value); setLocationId(''); }} className={INPUT_CLS}>
                  <option value="" disabled>Select cellar…</option>
                  {cellars.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              {locations.length > 0 && (
                <Field label="Location" hint="optional">
                  <select value={locationId} onChange={e => setLocationId(e.target.value)} className={INPUT_CLS}>
                    <option value="">None</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </Field>
              )}
            </div>
          </div>
        </div>

        {addFlow.isError && (
          <p className="mt-4 text-sm text-claret">{addFlow.error.message}</p>
        )}
      </form>

      {/* Sticky footer */}
      <div className="sticky bottom-0 z-30 bg-surface-1 border-t border-border-medium px-4 py-3">
        <div className="max-w-lg mx-auto w-full flex items-center justify-between gap-3">
          <span className="text-xs text-text-secondary truncate">
            {qty} × {year} <span dir="ltr">{displayName}</span>
            {costEur > 0 && <span className="text-text-primary"> · {eurFormat.format(totalEur)}</span>}
          </span>
          <button type="submit" onClick={handleSubmit} disabled={!canSubmit}
            className="flex-shrink-0 px-5 py-2 rounded-md bg-gold text-black font-semibold text-sm hover:bg-gold/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {addFlow.isPending ? 'Adding…' : qty > 1 ? `Add ${qty} bottles` : 'Add bottle'}
          </button>
        </div>
      </div>
    </div>
  );
}
